import { useEffect, useRef, useState } from "react";
import { reloadAndWipe, markUpdateLanded } from "../lib/cacheVersion";
import { useUnsavedGuardController } from "../context/UnsavedGuardContext";

// Poll interval: 5 minutes. Industry sweet spot per swarm Lane 7 — longer than 1min
// (avoids 200rpm cache load from concurrent users) but short enough that idle tabs
// notice new deploys within 5min without needing a refresh.
const POLL_INTERVAL_MS = 5 * 60 * 1000;

interface VersionManifest {
  commit: string;
  date: string;
}

/**
 * Polls /version.json every 5 minutes AND on tab focus. Compares the server's
 * current commit SHA against the SHA baked into the running bundle at build time
 * (window.__ALTARA_BUILD__.commit, set by index.tsx from __BUILD_COMMIT__).
 *
 * SMART RELOAD-WHEN-SAFE (replaces the old blocking window.confirm()):
 *   - Mount + visibility-return, tab is CLEAN (no unsaved form input per the
 *     UnsavedGuard registry) → arm a one-shot reload that fires on the user's
 *     NEXT navigation (hashchange). The reload rides a navigation they initiated
 *     anyway, so it's invisible; reloadAndWipe() preserves the destination route
 *     in the URL fragment, and a "New Altara update just landed" toast greets
 *     them on the fresh page (markUpdateLanded breadcrumb → UpdateLandedToast).
 *   - Mount + visibility-return, tab is DIRTY (a form holds unsaved input) →
 *     show the soft UpdateBanner instead. They finish, then click "Refresh now".
 *   - 5-min interval poll → soft banner (never interrupts active work).
 *
 * Dirtiness is re-checked AT navigation time, not just at detect time — a user
 * who starts typing after the arm gets the banner, not a surprise reload. There
 * is deliberately NO "reload when the tab is hidden" trigger: tab-hidden is not
 * a reliable "user is done" signal and would be the one path that could nuke a
 * form before its guard is wired. Reload only ever rides an explicit navigation.
 *
 * Dev/test safety: if __ALTARA_BUILD__ is missing or the commit is "unknown" (vite
 * fallback when git is unavailable), polling short-circuits and updateAvailable
 * stays false.
 */
export function useVersionPoll(): {
  updateAvailable: boolean;
  currentCommit: string | null;
  latestCommit: string | null;
} {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [latestCommit, setLatestCommit] = useState<string | null>(null);

  // Read-side of the global unsaved-work registry (stable api, or null if the
  // provider is somehow absent — then we treat the tab as always clean).
  const guard = useUnsavedGuardController();

  // Set true once a deploy is detected on a clean tab: reload on next navigation.
  const pendingReloadRef = useRef(false);

  const currentCommit =
    typeof window !== "undefined" &&
    (window as unknown as { __ALTARA_BUILD__?: { commit: string } }).__ALTARA_BUILD__
      ? (window as unknown as { __ALTARA_BUILD__: { commit: string } }).__ALTARA_BUILD__.commit
      : null;

  useEffect(() => {
    if (!currentCommit || currentCommit === "unknown") return;

    let cancelled = false;

    const hasUnsaved = () => guard?.hasUnsavedWork() ?? false;

    // Fire the deferred reload: breadcrumb for the post-reload toast, then the
    // 4-layer cache nuke (lib/cacheVersion.ts:reloadAndWipe).
    const doReload = () => {
      markUpdateLanded();
      reloadAndWipe();
    };

    const check = async (autoPrompt: boolean) => {
      try {
        const res = await fetch("/version.json", { cache: "no-store" });
        if (!res.ok) return;
        const data: VersionManifest = await res.json();
        if (cancelled) return;
        setLatestCommit(data.commit);
        if (!data.commit || data.commit === currentCommit) return;

        if (autoPrompt) {
          if (hasUnsaved()) {
            // Active form work — never reload from under them. Soft banner.
            setUpdateAvailable(true);
          } else {
            // Clean tab — defer a silent reload to the next navigation.
            pendingReloadRef.current = true;
          }
        } else {
          // 5-min interval path — always the soft banner, never interrupts.
          setUpdateAvailable(true);
        }
      } catch {
        // Network hiccups are fine — we'll try again in 5min or on tab focus.
      }
    };

    // HashRouter navigation = a `hashchange` on window. When the user navigates
    // after we've armed, fire the deferred reload — but re-check dirtiness first:
    // they may have started typing since the arm. Dirty now → banner instead.
    const onHashChange = () => {
      if (!pendingReloadRef.current) return;
      pendingReloadRef.current = false;
      if (hasUnsaved()) {
        setUpdateAvailable(true);
        return;
      }
      doReload();
    };

    // Mount + visibility-return = auto path (arm-or-banner); interval = banner.
    check(true);
    const interval = window.setInterval(() => check(false), POLL_INTERVAL_MS);
    const onVisibility = () => {
      if (document.visibilityState === "visible") check(true);
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("hashchange", onHashChange);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("hashchange", onHashChange);
    };
  }, [currentCommit, guard]);

  return { updateAvailable, currentCommit, latestCommit };
}
